import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'

dayjs.extend(weekOfYear)

export enum Time {
  TODAY = 'today',
  THIS_WEEKEND = 'thisWeekend',
  THIS_WEEK = 'thisWeek',
  NEXT_WEEKEND = 'nextWeekend',
  NEXT_WEEK = 'nextWeek',
  THIS_MONTH = 'thisMonth',
  NEXT_30_DAYS = 'nextThirtyDays',
  ANYTIME = 'anytime',
}

export interface TimeRange {
  start: Date
  end: Date
  anytime?: boolean
}

export function roundUnixTimestampToNearestMinutes(
  unixTimestamp: number,
  minutes: number = 5
): number {
  const milliseconds = unixTimestamp * 1000
  const interval = minutes * 60000
  return (Math.round(milliseconds / interval) * interval) / 1000
}

export function getTimeFromString(input: string): Time {
  return Object.values(Time).includes(input as Time) ? (input as Time) : Time.ANYTIME
}

export function getStringFromTime(input: Time): string {
  return input
}

// Returns the time range from the given Time enum
export function getTimeRangeFromTime(time: Time): TimeRange {
  const now = dayjs()

  switch (time) {
    case Time.TODAY:
      return {
        start: now.startOf('day').toDate(),
        end: now.endOf('day').toDate(),
        anytime: false,
      }

    case Time.THIS_WEEK:
      return {
        start: now.startOf('day').toDate(),
        end: now.add(7, 'day').endOf('day').toDate(),
        anytime: false,
      }

    case Time.THIS_WEEKEND:
      return {
        start: now.startOf('week').add(6, 'day').startOf('day').toDate(), // Saturday
        end: now.endOf('week').add(1, 'day').endOf('day').toDate(), // Sunday
        anytime: false,
      }

    case Time.NEXT_WEEK:
      const nextWeek = now.add(1, 'week')
      return {
        start: nextWeek.startOf('week').add(1, 'day').toDate(), // Next Monday
        end: nextWeek.endOf('week').add(1, 'day').toDate(), // Next Sunday
        anytime: false,
      }

    case Time.NEXT_WEEKEND:
      const nextWeekend = now.add(1, 'week')
      return {
        start: nextWeekend.startOf('week').add(6, 'day').startOf('day').toDate(), // Next Saturday
        end: nextWeekend.endOf('week').add(1, 'day').endOf('day').toDate(), // Next Sunday
        anytime: false,
      }

    case Time.THIS_MONTH:
      return {
        start: now.startOf('month').toDate(),
        end: now.endOf('month').toDate(),
        anytime: false,
      }

    case Time.NEXT_30_DAYS:
      return {
        start: now.startOf('day').toDate(),
        end: now.add(30, 'day').endOf('day').toDate(),
        anytime: false,
      }

    case Time.ANYTIME:
      return {
        start: now.startOf('year').toDate(),
        end: now.add(2000, 'year').endOf('year').toDate(), // To You, 2,000 Years From Now
        anytime: true,
      }

    default:
      return {
        start: now.startOf('year').toDate(),
        end: now.add(2000, 'year').endOf('year').toDate(),
        anytime: true,
      }
  }
}

// Returns the Time enum from the given time range
export function getTimeFromTimeRange(timeRange: TimeRange): Time {
  const now = dayjs()
  const startTime = dayjs(timeRange.start)
  const endTime = dayjs(timeRange.end)

  if (startTime.isSame(now, 'day') && endTime.isSame(now, 'day')) {
    return Time.TODAY
  }

  if (
    startTime.isSame(now.startOf('week').add(6, 'day'), 'day') &&
    endTime.isSame(now.endOf('week').add(1, 'day'), 'day')
  ) {
    return Time.THIS_WEEKEND
  }

  if (startTime.isSame(now, 'day') && endTime.isSame(now.add(7, 'day'), 'day')) {
    return Time.THIS_WEEK
  }

  if (
    startTime.isSame(now.add(1, 'week').startOf('week').add(1, 'day'), 'day') &&
    endTime.isSame(now.add(1, 'week').endOf('week').add(1, 'day'), 'day')
  ) {
    return Time.NEXT_WEEK
  }

  if (
    startTime.isSame(now.add(1, 'week').startOf('week').add(6, 'day'), 'day') &&
    endTime.isSame(now.add(1, 'week').endOf('week').add(1, 'day'), 'day')
  ) {
    return Time.NEXT_WEEKEND
  }

  if (startTime.isSame(now.startOf('day'), 'day') && endTime.isSame(now.add(30, 'day'), 'day')) {
    return Time.NEXT_30_DAYS
  }

  return Time.ANYTIME
}

export function convertFilterFacetToTimeRange(
  filter: number[] | undefined,
  skipMultiplicationBy1000: boolean = false
): TimeRange {
  if (!filter) {
    return getTimeRangeFromTime(Time.ANYTIME)
  }
  const start = new Date(filter[0] * (skipMultiplicationBy1000 ? 1 : 1000))
  const end = new Date(filter[1] * (skipMultiplicationBy1000 ? 1 : 1000))
  return { start, end, anytime: false }
}

export function convertTimeRangeToFilterFacet(
  tr: TimeRange,
  skipDivisionBy1000: boolean = false
): number[] | undefined {
  if (tr.anytime) return undefined
  const start = tr.start.getTime()
  const end = tr.end.getTime()
  return skipDivisionBy1000 ? [start, end] : [Math.floor(start / 1000), Math.floor(end / 1000)]
}

export function formatUnixTimestamp(unixTimestamp: number, format: number): number {
  const length = unixTimestamp.toString().length
  const diff = Math.abs(format - length)
  if (length > format) {
    return Math.floor(unixTimestamp / Math.pow(10, diff))
  }
  return Math.floor(unixTimestamp * Math.pow(10, diff))
}
